import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { cn } from "../../components/ui/cn";
import { dayMonth } from "../../components/meetings/MeetingRow";
import { fieldApi, fieldKeys, type FieldTask } from "../../lib/field-api";
import type { User } from "../../types";
import { FieldTabs } from "./FieldShell";

/**
 * The list behind Home's Pending tasks card: your open Lane tasks on the
 * sales boards, overdue first.
 *
 * Read-only on purpose. Lane is where a task is worked -- its comments, its
 * status columns, who else is on it -- and a second place to tick things off
 * would be a second place for them to disagree. Each row opens the task in
 * Lane.
 *
 * Under the Home tab rather than a tab of its own: it is the detail of one
 * card, the way the Recent feed is the detail of the meetings cards.
 */
export function FieldTasks({ user }: { user: User }) {
  const list = useQuery({
    queryKey: fieldKeys.tasks,
    queryFn: fieldApi.tasks,
    refetchOnMount: "always",
    refetchInterval: 5 * 60_000,
  });

  const tasks = list.data?.tasks ?? [];
  const overdue = tasks.filter((task) => task.overdue);
  const rest = tasks.filter((task) => !task.overdue);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FieldTabs user={user} tab="home" />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-5 sm:px-6">
          <Link
            to="/field/home"
            className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
          >
            ← Home
          </Link>
          <h1 className="mt-1 text-lg font-semibold text-content">Your pending tasks</h1>
          <p className="text-xs text-muted">
            Open tasks assigned to you on the sales boards in Lane. Overdue means past four o'clock on the day it was due.
          </p>

          {list.isLoading ? (
            <p className="mt-6 text-sm text-faint">Asking Lane…</p>
          ) : list.isError || list.data?.error ? (
            <p className="mt-6 text-sm text-muted">
              Lane didn't answer.{" "}
              <button
                type="button"
                onClick={() => void list.refetch()}
                className="text-brand-600 hover:underline dark:text-brand-400"
              >
                Try again
              </button>
            </p>
          ) : !list.data?.enabled ? (
            <p className="mt-6 text-sm text-muted">Lane isn't connected on this server.</p>
          ) : tasks.length === 0 ? (
            <p className="mt-6 text-sm text-muted">Nothing on your plate. ✓</p>
          ) : (
            <>
              {overdue.length > 0 ? (
                <Section title={`Overdue · ${overdue.length}`} tasks={overdue} />
              ) : null}
              {rest.length > 0 ? <Section title={`Open · ${rest.length}`} tasks={rest} /> : null}
            </>
          )}

          {list.data && list.data.unreadable > 0 ? (
            <p className="mt-4 text-[11px] text-faint">
              {list.data.unreadable === 1 ? "One board" : `${list.data.unreadable} boards`} could not be read, so this may be short.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Section({ title, tasks }: { title: string; tasks: FieldTask[] }) {
  return (
    <section className="mt-5">
      <h2 className="text-[11px] font-medium uppercase tracking-wide text-faint">{title}</h2>
      <ul className="mt-1.5 divide-y divide-line rounded-lg border border-line">
        {tasks.map((task) => (
          <li key={task.id}>
            <a
              href={task.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2 hover:bg-surface-2"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-content">{task.title}</span>
                <span className="block text-[11px] text-faint">{task.board}</span>
              </span>
              <span
                className={cn(
                  "shrink-0 text-xs tabular-nums",
                  task.overdue ? "font-medium text-amber-700 dark:text-amber-400" : "text-muted",
                )}
              >
                {task.dueAt ? `${task.overdue ? "Was due " : "Due "}${dayMonth(task.dueAt)}` : "No due date"}
              </span>
              <span aria-hidden className="text-faint">↗</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
